import { Accordion, Box, Button, ButtonsGroup, Card, Grid, Heading, InputColor, InputText, Textarea, Typo } from "#/atoms";
import { DESKTOP_SPACING, FONT_SIZE, HPOSITION, NATURAL, PRIMARY_BLUE } from "@/theme";
import { FC, memo, useEffect, useState } from "react";
import { HeadingIcon, RadioButtonGroup } from "#/molecules";

import { PlayerConfig } from "@/components/organisms/VideoPlayer/VideoPlayer.interface";
import { RadioButtonProps } from "@/components/molecules/RadioButtonGroup/RadioButtonGroup.interface";
import VideoPlayer from "@/components/organisms/VideoPlayer/VideoPlayer";
import { VideoServiceProps } from "./OrderService.interface";
import { config } from "../../../../../api/contants";
import { useInput } from "@/hooks";

const WalklyService: FC<VideoServiceProps> = ({
  amount,
  externalUrl,
  isAdmin,
  label,
  name,
  onChangeUrl,
  onChangeLabel,
  onShareVideo: onShareOrder,
  orderId,
  serviceId,
  uploadedFiles
}) => {
  const accordionState = 'open';

  const email = useInput();
  const url = `${config.server.clientUrl}/#/v/${orderId}`;
  const iframeSrc = `<iframe src="${config.server.clientUrl}/#/v/${orderId}" style="border: 0px; min-height:600px" />`;

  const logoTypes : RadioButtonProps[] = [{
        id: "1",
        label: "Client Logo",
        value: "1"
      },
      {
        id: "2",
        label: "Photoplan Logo",
        value: "2"
      }
    ]

  const getPlayerConfig = (videoUrl? :string, updatedLabel? :string) => { 
    const videoInfo = JSON.parse(updatedLabel || label) as PlayerConfig; 

    const videoSrc = uploadedFiles.length > 0 
              ? uploadedFiles[0].fullURL
              : videoUrl || externalUrl;

    return { autoPlay: false,
            clientLogoSize: videoInfo.clientLogoSize || 10,
            clientLogoSrc: videoInfo.clientLogoSrc,
            description: videoInfo.description,
            loop: false,
            primaryColor: videoInfo.primaryColor || '#ffffff',
            providerLogoSrc: videoInfo.providerLogoSrc,
            title: videoInfo.title,
            videoName: videoInfo.title,
            videoSrc: videoSrc }
  }

  const getLabelJson = (cfg: PlayerConfig) => JSON.stringify(cfg);

  const initialConfig = getPlayerConfig();

  const [playerConfig, setPlayerConfig] = useState(initialConfig);

  const logoType = useInput(initialConfig.clientLogoSrc ? '1' : '2' );

  useEffect(() => {
    setPlayerConfig(getPlayerConfig());
  }, [label, externalUrl]);

  const handleDescriptionChange = (e) => { 
    setPlayerConfig(c => ({...c, description: e.target.value }) );
    const newLabel = getLabelJson({...playerConfig, description: e.target.value });
    onChangeLabel?.(newLabel, serviceId);  
  }

  const handleTitleChange = (e) => { 
    setPlayerConfig(c => ({...c, title: e.target.value }) );
    const newLabel = getLabelJson({...playerConfig, title: e.target.value });
    onChangeLabel?.(newLabel, serviceId);  
  }

  const handlePrimaryColorChange = (e) => { 
    setPlayerConfig(c => ({...c, primaryColor: e.target.value }) );
    const newLabel = getLabelJson({...playerConfig, primaryColor: e.target.value });
    onChangeLabel?.(newLabel, serviceId);  
  }

  const handleLogoSizeChange = (e) => { 
    setPlayerConfig(c => ({...c, clientLogoSize: e.target.value }) );
    const newLabel = getLabelJson({...playerConfig, clientLogoSize: e.target.value });
    onChangeLabel?.(newLabel, serviceId);  
  }

  const handleLogoTypeChange = (groupName: string, selectedValue: string) => {
    const clientLogo = selectedValue == "1" ? 'https://photoplanet1.s3.eu-west-1.amazonaws.com/PhotoplanetExamples/redcross.png' : '';
    const providerLogo = selectedValue == "2" ? 'https://photoplanet1.s3.eu-west-1.amazonaws.com/PhotoplanetExamples/redcross.png' : '';

    setPlayerConfig(c => ({...c, clientLogoSrc: clientLogo, providerLogoSrc: providerLogo }) );
    const newLabel = getLabelJson({...playerConfig, clientLogoSrc: clientLogo, providerLogoSrc: providerLogo });
    onChangeLabel?.(newLabel, serviceId);
  }

  return (
    <Card>
        <Accordion
          header={<HeadingIcon icon="services" mb={`${DESKTOP_SPACING.XXL}px`} title={`${name} ${amount ? " X" + amount : ""}`} />}
          showArrow
          state={accordionState}
        >

         { isAdmin &&<> <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(2, 40%)" margin="0">
            <Box>
              <InputText
                type="text"
                label="Video URL"
                value={externalUrl}
                onChange={(e) => { onChangeUrl?.(e.target.value, serviceId); setPlayerConfig(getPlayerConfig(e.target.value));  }}
              />
              <Button>
                Click to Upload
              </Button>
            </Box>
              <InputText
                type="text"
                label="Title"
                value={playerConfig.title}
                onChange={handleTitleChange}
              />
              <InputText
                type="text"
                label="Description"
                value={playerConfig.description}
                onChange={handleDescriptionChange}
              />
              <InputColor
                label="Primary colour"
                value={playerConfig.primaryColor}
                onChange={handlePrimaryColorChange}
              />
              <div>
                <label htmlFor="clientLogoSlider" style={{color: `${NATURAL.G}`, fontSize: `${FONT_SIZE.XS}`,  marginRight: '10px'}}>Logo Size</label>
                <input id="clientLogoSlider" className="logo-opacity-slider" type="range" min="0" max="100" value={playerConfig.clientLogoSize} onChange={handleLogoSizeChange} style={{accentColor: `${PRIMARY_BLUE.DARK}` }}/>
                <output>{playerConfig.clientLogoSize}</output>
              </div>
              <Box margin="5px 0px 0px">
                <RadioButtonGroup groupName="sizeType" inputs={logoTypes} selectedValue={logoType.value} onChangeValue={handleLogoTypeChange} />
              </Box>
          </Grid> 
          <Heading scale="h3" margin="10px 0px" >Preview</Heading>
          </>
          }
        { <VideoPlayer config={playerConfig} /> }

        <Typo margin="15px 0px">Send a link to an email address where the recipient can view the video or get an embeddable version</Typo>
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="30%  30% 30%" margin="10px">
        <InputText
            type="email"
            label="Email"
            value={email?.value}
            onChange={email?.onChange}
          />
          <InputText
              fullWidth={false}
              label="Link"
              type="url"
              readOnly={true}
              value={url}
            />
          <Box>
            <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
              <Button onClick={() => navigator.clipboard.writeText(url)}>Copy</Button>
              <Button onClick={() => onShareOrder?.(email.value)}>
              Share via Email
              </Button>
            </ButtonsGroup>
          </Box>
        </Grid>
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="70% 30%" margin="10px">
        <Textarea
            label="Email"
            value={iframeSrc}
            readOnly={true}
          />
          <Box padding="22px 0 0">
            <Button onClick={() => navigator.clipboard.writeText(iframeSrc)}>Copy Embed Code</Button>
          </Box>
        </Grid>
        </Accordion>
    </Card>
  );
}

export default memo(WalklyService);